import Joi from "joi-browser";
import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { getFieldOptions, getFieldValue } from "../../scripts/formScriptsv2";
import {
  deactivateForm,
  getFormConfig,
  getSubmissionsByContainerIdForPrimaryObjectId,
  handleFormChange,
  loadSubmission,
  saveForm,
  saveRelatedObject,
  sendShareEmail,
  validateForm,
} from "../../scripts/formsyv2Scripts";
import BackIcon from "../common/BackIcon";
import Comment from "./common/Comment";
import FileUpload from "../common/FileUpload";
import FormButtonBar from "../common/FormButtonBar";
import FormHeader from "../common/FormHeader";
import ShareModal from "./common/ShareModal";
import { getAttachments, getSOARFosterHomeInfo } from "../services/soar";
import {
  FormInput,
  FormRadioButton,
  FormSelect,
  FormTextArea,
} from "./common/ControlBundlev2";
import Signature from "./common/Signature";
import FormComments from "./common/FormComments";
import { toast } from "react-toastify";

const versionId = "1106FCC1-0DD8-EF11-88F9-FC68272BBF38"; // FH-PIP Version Id

function FHPIP() {
  const location = useLocation();
  const navigate = useNavigate();
  const { fosterhomeid, submissionid } = useParams();
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [data, setData] = useState({});
  const [touched, setTouched] = useState({});
  const [timeoutId, setTimeoutId] = useState();
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  const [errors, setErrors] = useState({});
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showComment, setShowComment] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [controllerSignal, setControllerSignal] = useState();
  const pdfRef = useRef();
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

  const formsySignatures = useMemo(() => {
    const signatures = formsyDetails.Related?.filter(
      (r) => r.ObjectType === "Signature"
    ).map((r) => r.ObjectJSON);

    return signatures || [];
  }, [formsyDetails.Related]);

  const formDisabled = useMemo(() => {
    return !formsyDetails.Active || formsyDetails.FinalSigned;
  }, [formsyDetails.FinalSigned, formsyDetails.Active]);

  const helperConfiguration = useMemo(() => {
    const defaultConfig = {
      buttonBarConfig: {
        sign: {
          display: false,
          initialSignatureType: "full",
        },
        clone: {
          display: false,
        },
        delete: {
          display: false,
        },
        share: {
          display: false,
        },
        comment: {
          display: false,
        },
      },
      signatureType: "full",
      finalSignType: "Any",
    };

    return formsyConfig.HelperConfiguration || defaultConfig;
  }, [formsyConfig.HelperConfiguration]);

  useEffect(() => {
    const controller = new AbortController();
    setControllerSignal(controller.signal);
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!controllerSignal || controllerSignal.aborted) {
      return;
    }

    const load = async () => {
      const promises = [
        getFormConfig(versionId),
        getSOARFosterHomeInfo(fosterhomeid, controllerSignal),
      ];

      if (submissionid) {
        promises.push(loadSubmission(submissionid));
      }

      const responses = await Promise.all(promises);

      if (controllerSignal.aborted) {
        return;
      }

      if (responses) {
        const si = JSON.parse(localStorage.getItem("safyInfo"));

        const config = responses[0];
        const fosterHome = responses[1];
        const address = fosterHome.addresses[0];

        setFormsyConfig({
          ...config,
          FormDescription: "Foster Home Performance Improvement Plan",
          ContainerId: "0ADE98AF-0DD8-EF11-88F9-FC68272BBF38", // FHPIP Container Id
          SubmissionId: submissionid,
        });

        if (submissionid) {
          setData(responses[2].Results);
          setFormsyDetails(responses[2]);
        } else {
          //Set Formsy Details
          const newFormDetails = {
            SubmissionId: "",
            ContainerId: config.ContainerId,
            VersionId: config.VersionId,
            Results: {},
            Compliancy: null,
            Related: null,
            FinalSigned: false,
            FinalSignedDate: null,
            FinalSignedById: null,
            FinalSignedName: null,
            FormLocked: false,
            SignatureLocked: false,
            ModifiedById: si.user.azureId,
            ModifiedByName: si.user.displayName,
            Active: true,
          };
          setFormsyDetails(newFormDetails);

          const dataObj = { ...config.FormConfiguration };
          dataObj.FHName.value = fosterHome.facility.name;
          dataObj.FHDivision.value = fosterHome.facility.division;
          dataObj.FHAddress.value = `${address.line1} ${address.city}, ${address.state} ${address.zip}`;
          dataObj.FHLicenseStart.value = moment(
            fosterHome.license.start
          ).format("l");
          dataObj.FHLicenseRecert.value = fosterHome.license.recertification
            ? moment(fosterHome.license.recertification).format("l")
            : null;
          dataObj.FHFacilityType.value = fosterHome.facility.types
            .map((t) => t.text)
            .join(", ");
          dataObj.FHLicenseType.value = fosterHome.license.types
            .map((t) => t.text)
            .join(", ");
          setData(dataObj);
        }
      }
    };

    load();
  }, [controllerSignal, submissionid, fosterhomeid]);

  const joiSchema = {
    FHName: Joi.string().allow(""),
    FHDivision: Joi.string().allow(""),
    FHAddress: Joi.string().required().label("Foster Home Address"),
    FHLicenseStart: Joi.string().allow(""),
    FHLicenseRecert: Joi.string().allow(""),
    FHFacilityType: Joi.string().allow(""),
    FHLicenseType: Joi.string().allow(""),
    DateImplemented: Joi.string().required().label("Date Implemented"),
    ExpectedDate: Joi.string().required().label("Anticipated Completion Date"),
    RelatedToViolation: Joi.string()
      .required()
      .label("Is related to a Rules Violation"),
    FHRulesViolation: Joi.when("RelatedToViolation", {
      is: "Yes",
      then: Joi.string().required().label("Related Rules Violation"),
    }),
    Description: Joi.string().required().label("PIP Description"),
    OnHold: Joi.string().required().label("Has home been placed On Hold"),
    OnHoldDate: Joi.when("OnHold", {
      is: "Yes",
      then: Joi.string().required().label("Date placed On Hold"),
    }),
    MonitoredBy: Joi.string().required().label("Who is monitoring this PIP"),
    DateCompleted: Joi.string().required().label("Date Completed"),
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, joiSchema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, joiSchema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (dataObj, autoSave = false) => {
    if (isSaveInProgress) {
      return;
    }

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    setIsSaveInProgress(true);

    try {
      const formData = {
        ...formsyConfig,
        PrimaryObjectId: fosterhomeid,
        PrimaryObjectName: dataObj.FHName.value,
        PrimaryObjectType: "Foster Home",
        Results: { ...dataObj },
      };

      if (
        getFieldValue(dataObj.RelatedToViolation) === "Yes" &&
        getFieldValue(dataObj.FHRulesViolation)
      ) {
        const selectedOption = rulesViolationOptions.find(
          (rv) => rv._id === getFieldValue(dataObj.FHRulesViolation)
        );
        formData.SecondaryObjectId = selectedOption._id;
        formData.SecondaryObjectName = selectedOption.name;
        formData.SecondaryObjectType = "FH Rules Violation";
      }

      const submissionIdResponse = await saveForm(
        formData,
        !submissionid ? "Insert" : "Update"
      );

      if (submissionIdResponse?.code === "success") {
        if (!submissionid) {
          navigate(
            `/fosterhomes/${fosterhomeid}/pip/${submissionIdResponse.id}`,
            {
              replace: true,
              state: location.state,
            }
          );
        } else {
          setFormsyDetails((fd) => {
            const details = { ...fd };
            details.ModifiedOn = moment.utc().format();
            details.ModifiedById = safyInfo.user.azureId;
            details.ModifiedByName = safyInfo.user.displayName;
            return details;
          });
        }
      } else {
        console.log("Form Save Error", submissionIdResponse);
      }
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////DELETE
  const handleDelete = async (e) => {
    e.preventDefault();
    if (submissionid) {
      deactivateForm(submissionid);
      navigate(`/fosterhomes/${fosterhomeid}`, {
        replace: true,
        state: { ...location.state, tab: "rules" },
      });
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const getShareLogs = useCallback(() => {
    const comments = formsyDetails.Related?.filter(
      (r) => r.Active && r.ObjectType === "Share"
    );

    return comments || [];
  }, [formsyDetails.Related]);

  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

    saveRelatedObject(submissionid, {
      action: "insert",
      objectJson: {
        comment: shareComments,
        sharedBy: safyInfo.user.displayName,
        sharedTo: recipientList,
      },
      objectType: "Share",
    });
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const getAllComments = useCallback(() => {
    const comments = formsyDetails.Related?.filter(
      (r) => r.Active && r.ObjectType === "Comment"
    );

    return comments || [];
  }, [formsyDetails.Related]);

  const getFieldComments = useCallback(
    (field) => {
      return getAllComments().filter((c) => c.ObjectJSON.field === field);
    },
    [getAllComments]
  );

  const toggleCommentModal = (comment) => {
    setCommentToEdit(comment);
    setShowComment(!showComment);
  };

  const commentFields = useMemo(() => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form" });
    for (const property in formsyConfig.FormConfiguration) {
      if (
        formsyConfig.FormConfiguration[property] &&
        formsyConfig.FormConfiguration[property].type !== "section" &&
        formsyConfig.FormConfiguration[property].type !== "label" &&
        formsyConfig.FormConfiguration[property].type !== "User" &&
        formsyConfig.FormConfiguration[property].type !== "hidden"
      ) {
        const option = {
          _id: property,
          name: formsyConfig.FormConfiguration[property].label || property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  }, [formsyConfig.FormConfiguration]);

  const onCommentSave = async (comment) => {
    toggleCommentModal();
    setCommentToEdit(undefined);

    const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
    const relatedObj = {
      action: "insert",
      objectJson: {
        ...comment.ObjectJSON,
        createdBy: safyInfo.user.displayName,
        createdOn: moment().format("LLL"),
      },
      objectType: "Comment",
    };

    if (comment.RelatedId) {
      relatedObj.action = "update";
      relatedObj.relatedId = comment.RelatedId;
      const response = await saveRelatedObject(submissionid, relatedObj);

      if (response.code === "success") {
        toast.success("Comment saved");
        setFormsyDetails((fd) => {
          const obj = { ...fd };
          const originalIndex = obj.Related.findIndex(
            (r) => r.RelatedId === comment.RelatedId
          );
          obj.Related[originalIndex] = comment;
          return obj;
        });
      } else {
        toast.error("Error saving comment");
        console.log(response);
      }
    } else {
      const response = await saveRelatedObject(submissionid, relatedObj);
      if (response.code === "success") {
        toast.success("Comment saved");
        const newComment = {
          RelatedId: response.id,
          SubmissionId: submissionid,
          ObjectJSON: {
            ...relatedObj.objectJson,
          },
          ObjectType: "Comment",
          Active: true,
        };
        setFormsyDetails((fd) => {
          const obj = { ...fd };
          obj.Related.push(newComment);
          return obj;
        });
      } else {
        toast.error("Error saving comment");
      }
    }
  };

  const handleDeleteComment = async (comment) => {
    const response = await saveRelatedObject(submissionid, {
      action: "deactivate",
      relatedId: comment.RelatedId,
    });

    if (response.code === "success") {
      toast.success("Comment deleted");
      setFormsyDetails((fd) => {
        const obj = { ...fd };
        const originalIndex = obj.Related.findIndex(
          (r) => r.RelatedId === comment.RelatedId
        );
        obj.Related.splice(originalIndex, 1);
        return obj;
      });
    } else {
      toast.error("Error deleting comment");
    }
  };

  const handleAcknowledgeComment = async (comment, acknowledge) => {
    const commentObj = {
      action: "update",
      relatedId: comment.RelatedId,
      objectJson: comment.ObjectJSON,
      objectType: "Comment",
    };

    if (acknowledge) {
      commentObj.objectJson.acknowledgedBy = safyInfo.user.displayName;
      commentObj.objectJson.acknowledgedOn = moment().format("L LT");
    } else {
      delete commentObj.objectJson.acknowledgedBy;
      delete commentObj.objectJson.acknowledgedOn;
    }

    const response = await saveRelatedObject(submissionid, commentObj);

    if (response.code === "success") {
      toast.success("Comment acknowledged");
      setFormsyDetails((fd) => {
        const obj = { ...fd };
        const originalIndex = obj.Related.findIndex(
          (r) => r.RelatedId === comment.RelatedId
        );
        obj.Related[originalIndex].ObjectJSON = commentObj.objectJson;
        return obj;
      });
    } else {
      toast.error("Error acknowledging comment");
      console.log(response);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const commentsFactory = {
    add: toggleCommentModal,
    edit: toggleCommentModal,
    delete: handleDeleteComment,
    acknowledge: handleAcknowledgeComment,
    get: getFieldComments,
    navigate: handleCommentNavigation,
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    const relatedObj = {
      action: "insert",
      objectJson: signatureData,
      objectType: "Signature",
    };
    const response = await saveRelatedObject(
      formsyConfig.SubmissionId,
      relatedObj
    );
    if (response.code === "success") {
      setFormsyDetails((fd) => {
        const obj = { ...fd };

        if (!obj.Related) {
          obj.Related = [relatedObj];
        } else {
          obj.Related.push(relatedObj);
        }

        return obj;
      });
    }
  };

  //CUSTOM
  const [fileAttachments, setFileAttachments] = useState([]);

  const loadFiles = useCallback(async () => {
    if (submissionid) {
      const response = await getAttachments(submissionid);
      if (response?.data.length > 0) {
        setFileAttachments(response.data);
      }
    }
  }, [submissionid]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const [rulesViolations, setRulesViolations] = useState([]);

  const rulesViolationOptions = useMemo(() => {
    const options = rulesViolations.map((rv) => ({
      _id: rv.SubmissionId,
      name: `${moment(getFieldValue(rv.Results.DateOfViolation)).format(
        "l"
      )} - ${getFieldValue(rv.Results.Category)}`,
    }));

    return options;
  }, [rulesViolations]);

  useEffect(() => {
    if (controllerSignal && !controllerSignal.aborted) {
      const loadFHRulesViolations = async () => {
        const response = await getSubmissionsByContainerIdForPrimaryObjectId(
          "EC878651-B6CF-EF11-88D0-F28FA7FAF263", // FHRulesViolation Formsy Container Id
          fosterhomeid
        );

        if (response && !controllerSignal.aborted) {
          setRulesViolations(response);
        }
      };

      loadFHRulesViolations();
    }
  }, [controllerSignal, fosterhomeid]);

  const yesNoOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
  ];

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <FormHeader
          formTitle={formsyConfig.FormDescription}
          formsyDetails={formsyDetails}
        />
        <FormButtonBar
          config={helperConfiguration.buttonBarConfig}
          onSave={() => {
            handleSubmit(data);
          }}
          onDelete={submissionid ? handleDelete : null}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onShare={toggleShareModal}
          signed={formsySignatures.length > 0}
          finalSigned={formsyConfig.FinalSigned === "1"}
          validate={validate}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
          pdfRef={pdfRef}
          hidePrint={true}
        />
        <FormComments
          comments={getAllComments()}
          commentsFactory={commentsFactory}
        />
        <div className="mt-2">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="h5">Home Details</div>
              <div className="container-sm">
                <div className="row gy-2 mb-3">
                  <div className="col">
                    Foster Home Name
                    <br />
                    {getFieldValue(data.FHName)}
                  </div>
                  <div className="col">
                    Division
                    <br />
                    {getFieldValue(data.FHDivision)}
                  </div>
                </div>
                <FormInput
                  name="FHAddress"
                  label="Foster Home Address"
                  error={errors.FHAddress}
                  value={getFieldValue(data.FHAddress)}
                  onChange={handleChange}
                  commentsFactory={commentsFactory}
                  disabled={formDisabled}
                />
                <div className="row gy-2 mb-3">
                  <div className="col">
                    License Start Date
                    <br />
                    {getFieldValue(data.FHLicenseStart)}
                  </div>
                  <div className="col">
                    Facility Type(s)
                    <br />
                    {getFieldValue(data.FHFacilityType)}
                  </div>
                </div>
                <div className="row gy-2 mb-3">
                  <div className="col">
                    Next Recertification Date
                    <br />
                    {getFieldValue(data.FHLicenseRecert)}
                  </div>
                  <div className="col">
                    License Type(s)
                    <br />
                    {getFieldValue(data.FHLicenseType)}
                  </div>
                </div>
              </div>
              <div className="h5">Background Information</div>
              <div className="container-sm">
                <div className="row row-cols-1 row-cols-md-2 gy-2">
                  <div className="col">
                    <FormInput
                      name="DateImplemented"
                      label="Date implemented"
                      error={errors.DateImplemented}
                      value={getFieldValue(data.DateImplemented)}
                      onChange={handleChange}
                      commentsFactory={commentsFactory}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col">
                    <FormInput
                      name="ExpectedDate"
                      label="Anticipated completion date"
                      error={errors.ExpectedDate}
                      value={getFieldValue(data.ExpectedDate)}
                      onChange={handleChange}
                      commentsFactory={commentsFactory}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                </div>
                <div className="row row-cols-1 row-cols-md-2 gy-2">
                  <div className="col">
                    <FormRadioButton
                      name="RelatedToViolation"
                      label="Is this Performance Improvement Plan related to a Rules Violation?"
                      error={errors.RelatedToViolation}
                      value={getFieldValue(data.RelatedToViolation)}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsFactory={commentsFactory}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    {getFieldValue(data.RelatedToViolation) === "Yes" ? (
                      <FormSelect
                        name="FHRulesViolation"
                        label="Select the related Rules Violation from this list"
                        error={errors.FHRulesViolation}
                        value={getFieldValue(data.FHRulesViolation)}
                        options={rulesViolationOptions}
                        onChange={handleChange}
                        commentsFactory={commentsFactory}
                        disabled={formDisabled}
                      />
                    ) : null}
                  </div>
                </div>
                <FormTextArea
                  name="Description"
                  label="Description of the Performance Improvement Plan, including concrete action steps; knowledge/skills to be developed; and expected timelines"
                  error={errors.Description}
                  value={getFieldValue(data.Description)}
                  onChange={handleChange}
                  commentsFactory={commentsFactory}
                  disabled={formDisabled}
                />
                <div className="row row-cols-1 row-cols-md-2 gy-2">
                  <div className="col">
                    <FormRadioButton
                      name="OnHold"
                      label="Is the home being placed on hold until completion of this Performance Improvement Plan?"
                      error={errors.OnHold}
                      value={getFieldValue(data.OnHold)}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsFactory={commentsFactory}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    {getFieldValue(data.OnHold) === "Yes" ? (
                      <FormSelect
                        name="OnHoldDate"
                        label="Date the home was placed on hold"
                        error={errors.OnHoldDate}
                        value={getFieldValue(data.OnHoldDate)}
                        options={getFieldOptions(data.OnHoldDate)}
                        onChange={handleChange}
                        commentsFactory={commentsFactory}
                        disabled={formDisabled}
                      />
                    ) : null}
                  </div>
                </div>
                <div className="row row-cols-1 row-cols-md-2 gy-2 mt-2">
                  <div className="col">
                    <FormInput
                      name="MonitoredBy"
                      label="Who is accountable for monitoring this Performance Improvement Plan?"
                      error={errors.MonitoredBy}
                      value={getFieldValue(data.MonitoredBy)}
                      onChange={handleChange}
                      commentsFactory={commentsFactory}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormInput
                      name="DateCompleted"
                      label="Date FP completed PIP"
                      error={errors.DateCompleted}
                      value={getFieldValue(data.DateCompleted)}
                      onChange={handleChange}
                      commentsFactory={commentsFactory}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(data);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              signatures={formsySignatures}
              onSign={saveSignature}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={helperConfiguration.finalSignType}
              initialSignatureType={helperConfiguration.signatureType}
            />
            {submissionid ? (
              <FileUpload
                header={<div className="h5">Upload documentation here:</div>}
                objectId={submissionid}
                objectType="formsy v2"
                savedFiles={fileAttachments}
                printImg={true}
                loadFiles={loadFiles}
              />
            ) : null}
            <Comment
              isOpen={showComment}
              comment={commentToEdit}
              onHide={toggleCommentModal}
              onSave={onCommentSave}
              fields={commentFields}
            />
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
                shareLogs={getShareLogs()}
              />
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default FHPIP;
